import React, { useMemo } from 'react';
import useNews from '../hooks/useNews';
import usePagination from '../hooks/usePagination';
import HeroNews from '../components/news/HeroNews';
import NewsGrid from '../components/news/NewsGrid';
import NewsPagination from '../components/news/NewsPagination';
import Loader, { SkeletonGrid } from '../components/common/Loader';
import ErrorMessage from '../components/common/ErrorMessage';
import BulletinForm from '../components/common/BulletinForm';
import { useSiteSettings } from '../context/SiteSettingsContext';

const HomePage = ({ searchQuery }) => {
  const { eBulletinActive } = useSiteSettings();
  const { currentPage, goToPage } = usePagination(1);
  const { news, loading, error, refetch } = useNews(null, currentPage);
  
  // Filter news by search query
  const filteredNews = useMemo(() => {
    if (!searchQuery || !searchQuery.trim()) {
      return news;
    }
    const query = searchQuery.toLowerCase();
    return news.filter(item => 
      item.title.toLowerCase().includes(query) ||
      item.source.toLowerCase().includes(query)
    );
  }, [news, searchQuery]);

  // Separate hero news from others
  const heroNews = filteredNews[0];
  const otherNews = filteredNews.slice(1);

  if (loading && currentPage === 1) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <SkeletonGrid count={6} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ErrorMessage message={error} onRetry={refetch} />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Page Title */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          {searchQuery ? `"${searchQuery}" için sonuçlar` : 'En Son Haberler'}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {searchQuery 
            ? `${filteredNews.length} haber bulundu`
            : 'Farklı kaynaklardan en güncel haberler'
          }
        </p>
      </div>

      {filteredNews.length === 0 ? (
        <ErrorMessage 
          message={searchQuery 
            ? 'Aramanızla eşleşen haber bulunamadı.' 
            : 'Henüz haber bulunmuyor.'
          } 
        />
      ) : (
        <>
          {/* Hero News - Only show if not searching */}
          {!searchQuery && heroNews && (
            <HeroNews news={heroNews} />
          )}

          {/* News Grid */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
              {searchQuery ? 'Arama Sonuçları' : 'Tüm Haberler'}
            </h2>
            <NewsGrid news={searchQuery ? filteredNews : otherNews} />
          </section>

          {/* E-Bülten Abonelik Bölümü */}
          {!searchQuery && eBulletinActive && (
            <section className="my-10 rounded-2xl overflow-hidden bg-gradient-to-r from-blue-600 to-blue-800 dark:from-blue-700 dark:to-blue-900 shadow-lg">
              <div className="px-6 py-8 sm:px-10 sm:py-10 flex flex-col sm:flex-row items-center gap-6">
                {/* Metin */}
                <div className="flex-1 text-center sm:text-left">
                  <div className="flex items-center justify-center sm:justify-start gap-2 mb-2">
                    <svg className="w-5 h-5 text-blue-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span className="text-blue-200 text-sm font-medium uppercase tracking-wide">E-Bülten</span>
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-1">
                    Haberlerden Geri Kalmayın!
                  </h2>
                  <p className="text-blue-100 text-sm leading-relaxed">
                    Güncel haberleri, gelişmeleri ve özel içerikleri doğrudan
                    e-posta adresinize gönderelim.
                  </p>
                </div>

                {/* Form */}
                <div className="w-full sm:w-auto sm:min-w-[360px]">
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5">
                    <BulletinForm compact />
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Pagination */}
          {!searchQuery && (
            <NewsPagination
              currentPage={currentPage}
              onPageChange={goToPage}
              hasMore={news.length >= 10}
            />
          )}
        </>
      )}

      {loading && currentPage > 1 && (
        <div className="mt-8">
          <Loader text="Daha fazla haber yükleniyor..." />
        </div>
      )}
    </div>
  );
};

export default HomePage;
